import React, { createContext, useContext, useState, ReactNode } from 'react';

interface AIAgentContextType {
  isOpen: boolean;
  openSidebar: () => void;
  closeSidebar: () => void;
  toggleSidebar: () => void;
  openWithContext?: (context: string) => void;
}

const AIAgentContext = createContext<AIAgentContextType | undefined>(undefined);

export const AIAgentProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  // Always start closed to prevent layout issues on reload
  const [isOpen, setIsOpen] = useState(false);

  // Reset state on mount to prevent persisted state from hot reloads
  React.useEffect(() => {
    setIsOpen(false);
  }, []);

  const openSidebar = () => setIsOpen(true);
  const closeSidebar = () => setIsOpen(false);
  const toggleSidebar = () => setIsOpen((prev) => !prev);
  const openWithContext = (context: string) => {
    // Store context in sessionStorage for the sidebar to pick up
    sessionStorage.setItem('ai-context', context);
    setIsOpen(true);
  };

  return (
    <AIAgentContext.Provider
      value={{
        isOpen,
        openSidebar,
        closeSidebar,
        toggleSidebar,
        openWithContext,
      }}
    >
      {children}
    </AIAgentContext.Provider>
  );
};

export const useAIAgent = () => {
  const context = useContext(AIAgentContext);
  if (!context) {
    throw new Error('useAIAgent must be used within AIAgentProvider');
  }
  return context;
};

